import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { useContentfulWebPage } from 'marketing/api'
import styled from 'styled-components'
import { rem } from 'polished'
import Components from './Components'
import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { useAppSelector } from 'hooks/reduxHooks'
import HeadData from 'components/Common/HeadData'
import GeoContext from 'contexts/geoContext'
import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import ContentfulModal from './Modal/ContentfulModal'
import ModalContext from 'contexts/ModalContext'
import { useHistory } from 'react-router'
import useQueryParams from 'hooks/useQueryParams'
import GolfMerchandiseCategoryCarousel from './GolfMerchandiseCategoryCarousel'

const GOLF_CAMPAIGN_PATH_SUFFIX = '/campaign/golf'
const GOLF_INSERT_AFTER_TITLE = 'World-Class Courses, Expertly Curated'

function isOfferListCarousel(component: App.ContentfulWebPageComponent): component is App.ContentfulWebPageOfferCarousel {
  return component.contentTypeId === 'componentExperienceOfferListCarousel'
}

const ContentfulWebPageContainer = styled(Pane)`
  padding-bottom: ${rem(40)};
`

interface Props {
  topChildren?: React.ReactNode
  bottomChildren?: React.ReactNode
}

function ContentfulWebPage(props: Props) {
  const { currentRegionCode } = useContext(GeoContext)
  const pathname = useAppSelector((state) => state.router.location.pathname)
  const { data: pageData, isLoading } = useContentfulWebPage(pathname.slice(3), currentRegionCode)
  const showModal = useContext(ModalContext)
  const history = useHistory()
  const query = useQueryParams()
  const activeModalSlug = query.get('open_modal')

  const shouldInsertGolfCarousel = useMemo(() => {
    return pathname.toLowerCase().endsWith(GOLF_CAMPAIGN_PATH_SUFFIX)
  }, [pathname])

  const openModal = useCallback(async() => {
    const params = new URLSearchParams(history.location.search)
    params.delete('open_modal')
    const newSearch = params.toString()
    history.replace(`${history.location.pathname}${newSearch ? `?${newSearch}` : ''}`)
    await showModal(<ContentfulModal contentModals={pageData?.modalComponents} slug={activeModalSlug} />)
  }, [activeModalSlug, history, pageData, showModal])

  useEffect(() => {
    if (activeModalSlug) {
      openModal()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModalSlug])

  const contentfulComponents = useMemo(() => {
    if (!pageData?.components) {
      return []
    }

    return pageData.components.reduce<Array<React.ReactNode>>((elements, component, index) => {
      const componentKey = `${component.contentTypeId}-${index}`
      const componentElement = <div
        key={componentKey}
        id={componentKey}
        data-type={component.contentTypeId}
      >
        <Components {...component} />
      </div>

      elements.push(componentElement)

      if (shouldInsertGolfCarousel && isOfferListCarousel(component) && component.title === GOLF_INSERT_AFTER_TITLE) {
        elements.push(<GolfMerchandiseCategoryCarousel key="golf-merchandise-carousel" />)
      }

      return elements
    }, [])
  }, [pageData, shouldInsertGolfCarousel])

  if (isLoading) {
    return <LoadingIndicator />
  }
  if (!pageData) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  return (
    <>
      <HeadData title={pageData.seoTitle ? pageData.seoTitle : pageData.title} description={pageData.seoDescription} />
      <ContentfulWebPageContainer>
        <OnlyIfChildren>{props.topChildren}</OnlyIfChildren>
        {contentfulComponents}
        <OnlyIfChildren>{props.bottomChildren}</OnlyIfChildren>
      </ContentfulWebPageContainer>
    </>
  )
}

export default ContentfulWebPage
